import {Component, Input} from '@angular/core';
import {ModuloConfig} from '../../../_models/ModuloConfig';
import {LicenzeService} from '../../../_services/licenze.service';
import {SaveMessage} from '../../../_helpers/save-message';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {MenuAccessService} from '../../../_services/menu-access.service';
import {MenuVoce} from '../../../_helpers/menu-voci';

@Component({
  selector: 'app-licenze-config-item',
  templateUrl: './licenze-config-item.component.html',
  styleUrls: ['./licenze-config-item.component.scss'],
  standalone: false
})
export class LicenzeConfigItemComponent {
  @Input() permesso: ModuloConfig;
  @Input() organizzazioneId: number;
  @Input() licenzaId: number;
  @Input() expiryDate: Date;
  @Input() roleId = -1;
  @Input() operatoreId = -1;
  @Input() level = '';
  loading = false;

  constructor(
    private saveMessage: SaveMessage,
    private licenzeService: LicenzeService,
    private organizzazioniService: OrganizzazioniService,
    private menuAccess: MenuAccessService) { }

  /**
   * Voci di menù sbloccate da questo modulo, secondo i binding effettivi: rende leggibile cosa si
   * sta attivando spuntando il modulo. Lettura inversa del legame configurato in Moduli → Mappatura menù.
   */
  get vociDiMenu(): MenuVoce[] {
    return this.menuAccess.vociDelModulo(this.permesso?.codice ?? '');
  }

  changePermesso = () => this.permesso.isAvailable ? this.removePermesso() : this.addPermesso();

  addPermesso = async () => {
    if (this.isLicenzaScaduta()) {
      this.saveMessage.messageError('La licenza è scaduta. Non è possibile aggiungere il modulo.');
      return;
    }

    this.loading = true;
    try {
      await this.licenzeService.createPermessoAsync({
        moduloId: this.permesso.id,
        organizzazioneId: +this.organizzazioneId,
        roleId: this.roleId === -1 ? 0 : +this.roleId,
        operatoreId: this.operatoreId === -1 ? 0 : +this.operatoreId,
        activationDate: new Date(),
        expiryDate: new Date(),
        licenzaId: +this.licenzaId
      });
      this.permesso.isAvailable = true;
      const moduloCodice = this.getModuloCode();
      this.saveMessage.messageInfo(
        moduloCodice ? `Licenza ${moduloCodice} aggiornata con successo.` : 'Licenza aggiornata con successo.'
      );
      await this.refreshPermessiOrganizzazioneAttiva();
    } catch {
      this.saveMessage.messageError("Ci sono problemi con la licenza.");
    } finally {
      this.loading = false;
    }
  };

  removePermesso = async () => {
    this.loading = true;
    try {
      await this.licenzeService.deletePermessoAsync(
        this.permesso.id,
        +this.organizzazioneId,
        this.roleId === -1 ? 0 : +this.roleId,
        this.operatoreId === -1 ? 0 : +this.operatoreId
      );
      this.permesso.isAvailable = false;
      const moduloCodice = this.getModuloCode();
      this.saveMessage.messageInfo(
        moduloCodice ? `Licenza ${moduloCodice} rimossa con successo.` : 'Licenza rimossa con successo.'
      );
      await this.refreshPermessiOrganizzazioneAttiva();
    } catch {
      this.saveMessage.messageError("Ci sono problemi con la licenza.");
    } finally {
      this.loading = false;
    }
  };

  private getModuloCode(): string {
    return (this.permesso?.codice ?? '').trim();
  }

  private isLicenzaScaduta(): boolean {
    const expiryTime = new Date(String(this.expiryDate)).getTime();
    return Number.isFinite(expiryTime) && expiryTime <= Date.now();
  }

  private async refreshPermessiOrganizzazioneAttiva(): Promise<void> {
    const organizzazioneId = +this.organizzazioneId;
    if (!organizzazioneId || organizzazioneId !== this.organizzazioniService.organizzazioneId) {
      return;
    }

    try {
      await this.licenzeService.refreshModuliOfOrgAsync(organizzazioneId);
    } catch {
      this.saveMessage.messageWarning(
        'Licenza salvata, ma i permessi correnti non sono stati aggiornati. Ricaricare la pagina.'
      );
    }
  }

}
